/**
 * ClarifyCard — the agent's clarifying questions, rendered inside the chat.
 *
 * The agent may answer with up to four questions instead of guessing (which
 * portfolio, which basis, which date range …). Each question renders as a panel
 * of options - radio for a single pick, checkbox when `multiSelect` - plus an
 * always-present "Other" row with a free-text field, the way Claude Code's own
 * clarifying questions work.
 *
 * Submitting sends a NORMAL chat message whose text the agent parses:
 *
 *     Clarifications:
 *     - <question text>: <answer>
 *
 * (one line per question; multi-select answers are comma-joined; an "Other"
 * pick contributes the typed text). The card then collapses to a compact
 * summary of what was chosen, persisted by assistant message id in
 * utils/clarifyStore so leaving and returning to the chat does not resurrect an
 * answered card. Cards on older messages (anything but the latest assistant
 * turn) render read-only: answering them would be answering a stale question.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Check, MessageSquarePlus } from 'lucide-react';
import { getClarifyAnswer, setClarifyAnswer } from '../utils/clarifyStore';

/** The exact contract text the backend parses out of the follow-up turn. */
export function buildClarificationsText(rows) {
  return ['Clarifications:', ...rows.map((r) => `- ${r.question}: ${r.answer}`)].join('\n');
}

/** Questions with a guaranteed id + option list, or [] when there is nothing to ask. */
function normalizeQuestions(clarify) {
  const list = Array.isArray(clarify?.questions) ? clarify.questions : [];
  return list.map((q, i) => ({
    ...q,
    id: q.id ?? `q${i}`,
    options: Array.isArray(q.options) ? q.options : [],
  }));
}

const emptyPick = { options: [], otherChecked: false, other: '' };

/** A question is answered by any option pick, or by Other with non-empty text. */
const isAnswered = (pick) =>
  pick.options.length > 0 || (pick.otherChecked && pick.other.trim().length > 0);

/** Selected labels in option order, with the typed Other text last. */
function answerText(question, pick) {
  const chosen = question.options
    .map((o) => o.label)
    .filter((label) => pick.options.includes(label));
  const other = pick.other.trim();
  if (pick.otherChecked && other) chosen.push(other);
  return chosen.join(', ');
}

export default function ClarifyCard({ clarify, darkMode, onSend, messageId, readOnly = false }) {
  const questions = useMemo(() => normalizeQuestions(clarify), [clarify]);
  const [submitted, setSubmitted] = useState(() => getClarifyAnswer(messageId));
  const [picks, setPicks] = useState({});

  // messageId can arrive after mount (the assistant serverId lands on the final
  // stream event); restore the prior answer then.
  useEffect(() => {
    if (submitted == null) {
      const saved = getClarifyAnswer(messageId);
      if (saved) setSubmitted(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId]);

  const pickFor = (id) => picks[id] || emptyPick;
  const update = (id, patch) =>
    setPicks((prev) => ({ ...prev, [id]: { ...(prev[id] || emptyPick), ...patch } }));

  const toggleOption = (question, label) => {
    const pick = pickFor(question.id);
    if (question.multiSelect) {
      const next = pick.options.includes(label)
        ? pick.options.filter((l) => l !== label)
        : [...pick.options, label];
      update(question.id, { options: next });
      return;
    }
    update(question.id, { options: [label], otherChecked: false });
  };

  const toggleOther = (question) => {
    const pick = pickFor(question.id);
    if (question.multiSelect) {
      update(question.id, { otherChecked: !pick.otherChecked });
      return;
    }
    update(question.id, { options: [], otherChecked: true });
  };

  // Typing an Other answer selects Other. On a single-select that also clears
  // the option pick - otherwise the built answer would carry both.
  const setOther = (question, value) =>
    update(question.id, {
      other: value,
      otherChecked: true,
      ...(question.multiSelect ? {} : { options: [] }),
    });

  const complete = questions.length > 0 && questions.every((q) => isAnswered(pickFor(q.id)));

  const submit = () => {
    if (!complete) return;
    const rows = questions.map((q) => ({
      id: q.id,
      header: q.header || '',
      question: q.question,
      answer: answerText(q, pickFor(q.id)),
    }));
    setSubmitted(rows);
    setClarifyAnswer(messageId, rows);
    if (typeof onSend === 'function') onSend(buildClarificationsText(rows));
  };

  if (questions.length === 0) return null;

  const muted = darkMode ? 'text-white/50' : 'text-gray-500';
  // Native controls, tinted to the chat's monochrome palette (the browser's
  // default blue accent reads as a foreign element on the dark surface).
  const controlClass = `shrink-0 ${darkMode ? 'accent-white' : 'accent-gray-900'}`;
  const inputVars = darkMode
    ? { '--input-bg': 'rgba(255,255,255,0.06)', '--input-fg': '#FFFFFF', '--input-placeholder': 'rgba(255,255,255,0.4)' }
    : { '--input-bg': 'rgba(0,0,0,0.04)', '--input-fg': '#0A0A0A', '--input-placeholder': 'rgba(0,0,0,0.35)' };

  // ---- Answered: collapse to what was chosen. ----
  if (submitted) {
    return (
      <div className="mt-3 flex flex-col items-start gap-1">
        {submitted.map((row) => (
          <div
            key={row.id}
            className={`inline-flex items-start gap-2 text-sm ${darkMode ? 'text-white/70' : 'text-gray-600'}`}
          >
            <Check className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
            <span>
              <span className={muted}>{row.header || row.question}: </span>
              {row.answer}
            </span>
          </div>
        ))}
      </div>
    );
  }

  // ---- Unanswered: the question panels (+ submit). ----
  return (
    <div className={`mt-3 w-full max-w-2xl ${readOnly ? 'opacity-60' : ''}`}>
      <div className="flex flex-col gap-3">
        {questions.map((question) => {
          const pick = pickFor(question.id);
          const inputType = question.multiSelect ? 'checkbox' : 'radio';
          return (
            <div
              key={question.id}
              className={`rounded-xl border overflow-hidden divide-y ${
                darkMode
                  ? 'border-white/15 divide-white/10 bg-white/5'
                  : 'border-gray-200 divide-gray-100 bg-black/[0.02]'
              }`}
            >
              <div className="flex items-center gap-2 px-4 py-3">
                {question.header && (
                  <span
                    className={`shrink-0 px-1.5 py-0.5 rounded-md text-[11px] uppercase tracking-wide ${
                      darkMode ? 'bg-white/10 text-white/70' : 'bg-black/[0.06] text-gray-600'
                    }`}
                  >
                    {question.header}
                  </span>
                )}
                <span className={`text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {question.question}
                </span>
              </div>

              {question.options.map((option) => (
                <label
                  key={option.label}
                  className={`flex items-start gap-3 px-4 py-3 text-left transition-colors ${
                    readOnly ? 'cursor-default' : `cursor-pointer ${darkMode ? 'hover:bg-white/5' : 'hover:bg-black/5'}`
                  }`}
                >
                  <input
                    type={inputType}
                    name={`clarify-${messageId ?? 'new'}-${question.id}`}
                    disabled={readOnly}
                    checked={pick.options.includes(option.label)}
                    onChange={() => toggleOption(question, option.label)}
                    className={`mt-0.5 ${controlClass}`}
                  />
                  <span className="min-w-0">
                    <span className={`block text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {option.label}
                    </span>
                    {option.description && (
                      <span className={`block text-xs ${muted}`}>{option.description}</span>
                    )}
                  </span>
                </label>
              ))}

              <div className="flex items-center gap-3 px-4 py-3">
                <label className={`flex items-center gap-3 ${readOnly ? 'cursor-default' : 'cursor-pointer'}`}>
                  <input
                    type={inputType}
                    name={`clarify-${messageId ?? 'new'}-${question.id}`}
                    disabled={readOnly}
                    checked={pick.otherChecked}
                    onChange={() => toggleOther(question)}
                    className={controlClass}
                  />
                  <MessageSquarePlus className={`w-4 h-4 shrink-0 ${muted}`} />
                  <span className={`text-sm ${darkMode ? 'text-white/80' : 'text-gray-700'}`}>Other</span>
                </label>
                <input
                  type="text"
                  aria-label={`Other answer for ${question.question}`}
                  disabled={readOnly}
                  value={pick.other}
                  onChange={(e) => setOther(question, e.target.value)}
                  placeholder="Type your own answer…"
                  // App.css paints every input with an !important background off
                  // the nearest `.dark` ancestor; this card themes off its own
                  // darkMode prop instead, so it declares the documented
                  // --input-* overrides rather than a (losing) bg utility.
                  style={inputVars}
                  className="flex-1 min-w-0 px-2 py-1 rounded-md text-sm outline-none"
                />
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={submit}
        disabled={readOnly || !complete}
        className={`mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
          darkMode
            ? 'border-white/20 text-white hover:bg-white/10'
            : 'border-gray-300 text-gray-900 hover:bg-gray-100'
        }`}
      >
        <Check className="w-4 h-4 shrink-0" />
        Submit answers
      </button>
    </div>
  );
}
